import { auth } from '@fleetops/firebase'
import { extractErrorMessage } from './apiError'

// Single place where a call to the Cloudflare Worker is made. Before this,
// the same four steps — read VITE_API_BASE_URL, get a fresh Firebase ID
// token, attach the Bearer header, turn a non-2xx body into a Portuguese
// message — were copy-pasted across ten hooks and components, each with
// slightly different error handling.

export const API_BASE = import.meta.env['VITE_API_BASE_URL'] as string

export function hasApi(): boolean {
  return !!API_BASE
}

export async function getIdToken(): Promise<string> {
  const user = auth.currentUser
  if (!user) throw new Error('Sessão terminada — inicie sessão novamente')
  return user.getIdToken()
}

export interface ApiFetchInit {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /** Plain object (sent as JSON) or FormData (sent as-is, no Content-Type). */
  body?: unknown
  /** Message shown when the Worker's own error body can't be read. */
  fallbackError?: string
  signal?: AbortSignal
}

/**
 * Calls the Worker and throws an `Error` carrying a user-presentable message
 * when the response isn't OK. Returns the raw `Response` — use `apiJson` when
 * you want the parsed body.
 */
export async function apiFetch(path: string, init: ApiFetchInit = {}): Promise<Response> {
  if (!API_BASE) throw new Error('API não configurada')
  const { method = 'GET', body, fallbackError = 'Erro de comunicação com o servidor', signal } = init

  const token = await getIdToken()
  const isFormData = typeof FormData !== 'undefined' && body instanceof FormData
  const headers: Record<string, string> = { Authorization: `Bearer ${token}` }
  // FormData must set its own multipart boundary — never override it.
  if (body !== undefined && !isFormData) headers['Content-Type'] = 'application/json'

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    ...(body !== undefined ? { body: isFormData ? (body) : JSON.stringify(body) } : {}),
    ...(signal ? { signal } : {}),
  })

  if (!res.ok) throw new Error(await extractErrorMessage(res, fallbackError))
  return res
}

export async function apiJson<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const res = await apiFetch(path, init)
  return res.json() as Promise<T>
}

/**
 * Fire-and-forget: notifications, auto-approval evaluation and similar side
 * effects that must never fail the user's action. Swallows every error after
 * logging it, and no-ops when the API isn't configured or nobody is signed in.
 */
export function apiFireAndForget(path: string, init: ApiFetchInit = {}): void {
  if (!API_BASE || !auth.currentUser) return
  void apiFetch(path, init).catch((e: unknown) => {
    console.error(`apiFireAndForget ${path} failed:`, e)
  })
}
